import { useEffect, useId, useRef } from 'react'
import type { ReminderInlineVoiceTranscribeConfig } from './ReminderPreviewInlineEdit'
import { useInlineFieldSpeechRecognition } from '../hooks/useInlineFieldSpeechRecognition'

export type NoteTaskPreviewInlineEditProps = {
  editing?: boolean
  inlineDraft?: string
  inlineSaving?: boolean
  inlineError?: string | null
  voiceTranscribe?: ReminderInlineVoiceTranscribeConfig
  onInlineDraftChange?: (value: string) => void
  onInlineSave?: () => void
  onInlineCancel?: () => void
}

export function NoteTaskContentInlineEditRow(props: {
  fr: boolean
  draft: string
  saving: boolean
  error: string | null
  languageHint?: string
  voiceTranscribe?: ReminderInlineVoiceTranscribeConfig
  onDraftChange: (value: string) => void
  onSave: () => void
  onCancel: () => void
}) {
  const { fr, draft, saving, error, languageHint, voiceTranscribe, onDraftChange, onSave, onCancel } =
    props
  const textareaId = useId()
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const { supported, status, statusHint, error: speechError, toggle } = useInlineFieldSpeechRecognition({
    websocketUrl: voiceTranscribe?.websocketUrl ?? '',
    apiBaseUrl: voiceTranscribe?.apiBaseUrl,
    transcribeMode: voiceTranscribe?.transcribeMode,
    getMedicalLinkCredentials: voiceTranscribe?.getMedicalLinkCredentials,
    clientId: voiceTranscribe?.clientId,
    tenantId: voiceTranscribe?.tenantId,
    sessionId: voiceTranscribe?.sessionId,
    languageHint,
    disabled: saving,
    blocked: voiceTranscribe?.blocked,
    onBusyChange: voiceTranscribe?.onBusyChange,
    onTranscript: (text) => onDraftChange(text),
  })

  useEffect(() => {
    textareaRef.current?.focus()
    textareaRef.current?.select()
  }, [])

  const micBlocked = Boolean(voiceTranscribe?.blocked)
  const helper =
    speechError ||
    (micBlocked && status !== 'listening'
      ? fr
        ? 'Micro principal occupé — réessayez dans un instant.'
        : 'Microphone busy — try again in a moment.'
      : status === 'listening'
        ? statusHint.trim() || (fr ? 'Écoute…' : 'Listening…')
        : fr
          ? 'Appuyez sur le micro pour parler ou saisissez votre texte.'
          : 'Tap mic to speak or type to edit.')
  const micLabel =
    status === 'listening'
      ? fr
        ? 'Écoute en cours'
        : 'Listening'
      : fr
        ? 'Dicter la tâche'
        : 'Dictate task'

  return (
    <div className="vnw-sportsNocKvRow vnw-reminderInlineEditRow" role="listitem">
      <div className="vnw-sportsNocKvKey">{fr ? 'Tâche' : 'Task'}</div>
      <div className="vnw-reminderInlineEditBody">
        <label className="vnw-reminderInlineEditLabel" htmlFor={textareaId}>
          {fr ? 'Modifier la tâche' : 'Edit task'}
        </label>
        <div className="vnw-reminderInlineEditInputWrap vnw-sportsNocCommentsInlineWrap">
          <textarea
            id={textareaId}
            ref={textareaRef}
            className="vnw-sportsNocCommentsTextarea vnw-sportsNocCommentsTextarea--inline"
            rows={3}
            disabled={saving}
            placeholder={fr ? 'Saisissez ici ou utilisez le micro…' : 'Type here or use the microphone…'}
            value={draft}
            onChange={(e) => onDraftChange(e.target.value)}
          />
          <button
            type="button"
            className={
              'vnw-reminderInlineEditMic' + (status === 'listening' ? ' vnw-reminderInlineEditMic--active' : '')
            }
            aria-label={micLabel}
            title={supported ? micLabel : fr ? 'Saisie vocale indisponible' : 'Voice input unavailable'}
            disabled={saving || !supported || (micBlocked && status !== 'listening')}
            aria-pressed={status === 'listening'}
            onClick={() => toggle()}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
              <path
                fill="currentColor"
                d="M12 14a3 3 0 0 0 3-3V5a3 3 0 1 0-6 0v6a3 3 0 0 0 3 3Zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.93V21h2v-3.07A7 7 0 0 0 19 11h-2Z"
              />
            </svg>
          </button>
        </div>
        <p className="vnw-reminderInlineEditHelper" role="status">
          {helper}
        </p>
        {error ? (
          <p className="vnw-reminderInlineEditHelper vnw-reminderInlineEditHelper--error" role="alert">
            {error}
          </p>
        ) : null}
        <div className="vnw-reminderInlineEditActions">
          <button
            type="button"
            className="vnw-reminderInlineEditBtn vnw-reminderInlineEditBtn--primary"
            disabled={saving || !draft.trim()}
            onClick={onSave}
          >
            {fr ? 'Enregistrer' : 'Save'}
          </button>
          <button
            type="button"
            className="vnw-reminderInlineEditBtn vnw-reminderInlineEditBtn--ghost"
            disabled={saving}
            onClick={onCancel}
          >
            {fr ? 'Annuler' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  )
}
